// ═══════════════════════════════════════════════════════════════════
// vaelActing.js — V'AEL ACTS   (caP'n, 2026-09-24)
// ═══════════════════════════════════════════════════════════════════
// caP'n: "let's give the blender V'ael in the Terror Plaza all these
// proper animations and expressions … we are going to use her in cut
// scenes so we need her to be able to make all these fun faces and
// movements :3"
//
// ONE WORD DRIVES HER: a MOOD. `<VaelPortrait3D mood="flustered" />`, or
// ctl.mood = "flustered" on any VaelModel mounted with `acting`. A mood is
// a face + a move + props + pop-ups, all in the MOODS table below:
//
//   neutral   her Plaza self (the smirk, the idle)
//   furious   glare + gritted teeth, rolling up her sleeve, shaking   (pic 1)
//   seething  eyes squeezed shut, fists, a big 💢                       (pic 2)
//   flustered red to the ears, teary, jabbing a finger at you, huffing (pic 3)
//   sulk      arms crossed, pout, side-eye                              (pic 4)
//   smug      sundae in hand, chocolate on her face, very pleased       (pic 5)
//   yumm      spoon to her mouth, delighted                             (pic 6)
//   bliss     spoon in her mouth, strolling, melting                    (pic 7)
//   laugh     hand on her forehead, eyes ^ ^, laughing                  (pic 8)
//   scold     yelling, beckoning you over                               (pic 9)
//   cackle    hand on her forehead, sly side-eye, laughing at you       (pic 10)
//
// And on top of ANY mood:
//   · she BLINKS on her own (unless the look already has shut eyes;
//     ctl.blink = false holds her eyes open for a still)
//   · ctl.talking = true flaps her mouth (the look's own mouth between flaps)
//   · ctl.eyes / ctl.mouth / ctl.fx override one piece of the mood's face,
//     so a scene can mix and match ("scold" with a blush: fx {blush: 1})
//
// THE FACE is painted, like her base face: face layers from
// gamemaster/models/sculpt/builders/vael_blender/face/face_expr.py, packed
// into public/models/tex/vael_faces.png (eyes + mouths) and vael_fx.png
// (blush, tears, sweat, chocolate, anger marks, gloom). They are laid OVER
// her base face in the skin's shader; nothing loads until a body acts, and
// if they never arrive she simply keeps her base face.
//
// THE BODY: every move is a pose function here (they own the body, we own
// the pose). Her fingers ride the 15-bones-per-hand rig (vaelHands.js).
//
// PERF LAW: nothing here sets React state from a frame; nothing allocates
// per frame after the first.
// ═══════════════════════════════════════════════════════════════════
import * as THREE from "three";
import { poseHand } from "./vaelHands";

const BASE = `${process.env.PUBLIC_URL || ""}/models/tex`;

// ── THE FACE LAYERS (printed by face/pack_atlas.py → vael_faces.json) ──
// layers: [u, v(top-down), du, dv] of the face texture; cells: [atlas, u, v, du, dv]
export const FACE = {"layers":{"eyes":[0.05462,0.15677,0.90408,0.40921],"mouth":[0.28302,0.61833,0.4568,0.28074],"fx":[0.04511,0.08064,0.93263,0.86601]},"cells":{"eyes:glare":["A",0.0,0.0,0.32568,0.14746],"eyes:seethe":["A",0.32764,0.0,0.32568,0.14746],"eyes:flustered":["A",0.65527,0.0,0.32568,0.14746],"eyes:sulk":["A",0.0,0.14941,0.32568,0.14746],"eyes:smug":["A",0.32764,0.14941,0.32568,0.14746],"eyes:delight":["A",0.65527,0.14941,0.32568,0.14746],"eyes:bliss":["A",0.0,0.29883,0.32568,0.14746],"eyes:laugh":["A",0.32764,0.29883,0.32568,0.14746],"eyes:yell":["A",0.65527,0.29883,0.32568,0.14746],"eyes:sly":["A",0.0,0.44824,0.32568,0.14746],"eyes:blink":["A",0.32764,0.44824,0.32568,0.14746],"eyes:half":["A",0.65527,0.44824,0.32568,0.14746],"mouth:grit":["A",0.0,0.59766,0.16455,0.10107],"mouth:frown":["A",0.1665,0.59766,0.16455,0.10107],"mouth:pout":["A",0.33301,0.59766,0.16455,0.10107],"mouth:smug":["A",0.49951,0.59766,0.16455,0.10107],"mouth:delight":["A",0.66602,0.59766,0.16455,0.10107],"mouth:munch":["A",0.83252,0.59766,0.16455,0.10107],"mouth:laugh":["A",0.0,0.70068,0.16455,0.10107],"mouth:yell":["A",0.1665,0.70068,0.16455,0.10107],"mouth:grin":["A",0.33301,0.70068,0.16455,0.10107],"mouth:talk_a":["A",0.49951,0.70068,0.16455,0.10107],"mouth:talk_o":["A",0.66602,0.70068,0.16455,0.10107],"mouth:talk_e":["A",0.83252,0.70068,0.16455,0.10107],"fx:blush":["B",0.0,0.0,0.24268,0.4502],"fx:blush_hard":["B",0.24463,0.0,0.24268,0.4502],"fx:tears":["B",0.48926,0.0,0.24268,0.4502],"fx:sweat":["B",0.73389,0.0,0.24268,0.4502],"fx:choc":["B",0.0,0.4541,0.24268,0.4502],"fx:veins":["B",0.24463,0.4541,0.24268,0.4502],"fx:gloom":["B",0.48926,0.4541,0.24268,0.4502]}};
export const FX_NAMES = ["blush", "blush_hard", "tears", "sweat", "choc", "veins", "gloom"];

let FACE_TEX = null;              // shared by every acting body: {A, B, ready}
function faceTextures() {
  if (FACE_TEX) return FACE_TEX;
  const L = new THREE.TextureLoader();
  const T = { A: null, B: null, ready: 0 };
  const done = (k) => (t) => {
    t.flipY = false; t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4; t.needsUpdate = true;
    T[k] = t; T.ready++;
  };
  const fail = (k) => (e) => console.warn("[vael] face layer " + k + " did not load — she keeps her base face", e);
  L.load(`${BASE}/vael_faces.png`, done("A"), undefined, fail("vael_faces.png"));
  L.load(`${BASE}/vael_fx.png`, done("B"), undefined, fail("vael_fx.png"));
  FACE_TEX = T;
  return T;
}

// the GLSL that lays the layers over the base face (map AND emissiveMap: her
// self-light samples the face too, and would glow the OLD eyes through)
const FACE_GLSL = `
uniform sampler2D vfA; uniform sampler2D vfB;
uniform vec4 vfEyeRect; uniform vec4 vfEyeCell; uniform float vfEyeOn;
uniform vec4 vfMouthRect; uniform vec4 vfMouthCell; uniform float vfMouthOn;
uniform vec4 vfFxRect; uniform vec4 vfFxCell[7]; uniform float vfFxAmt[7];
float vfIn(vec2 l, float f) {
  vec2 a = smoothstep(vec2(0.0), vec2(f), l) * smoothstep(vec2(0.0), vec2(f), 1.0 - l);
  return a.x * a.y;
}
vec3 vaelFace(vec3 c, vec2 uv) {
  if (vfEyeOn > 0.5) {
    vec2 l = (uv - vfEyeRect.xy) / vfEyeRect.zw;
    vec3 e = texture2D(vfA, vfEyeCell.xy + clamp(l, 0.0, 1.0) * vfEyeCell.zw).rgb;
    c = mix(c, e, vfIn(l, 0.035));
  }
  if (vfMouthOn > 0.5) {
    vec2 l = (uv - vfMouthRect.xy) / vfMouthRect.zw;
    vec3 m = texture2D(vfA, vfMouthCell.xy + clamp(l, 0.0, 1.0) * vfMouthCell.zw).rgb;
    c = mix(c, m, vfIn(l, 0.06));
  }
  vec2 lf = (uv - vfFxRect.xy) / vfFxRect.zw;
  float inF = vfIn(lf, 0.01);
  for (int i = 0; i < 7; i++) {
    if (vfFxAmt[i] > 0.002) {
      vec4 f = texture2D(vfB, vfFxCell[i].xy + clamp(lf, 0.0, 1.0) * vfFxCell[i].zw);
      c = mix(c, f.rgb, f.a * vfFxAmt[i] * inF);
    }
  }
  return c;
}
`;

const cellVec = (key) => {
  const c = FACE.cells[key];
  return c ? new THREE.Vector4(c[1], c[2], c[3], c[4]) : null;
};
const CELL = {};
for (const k of Object.keys(FACE.cells)) CELL[k] = cellVec(k);
const rectVec = (k) => { const r = FACE.layers[k]; return new THREE.Vector4(r[0], r[1], r[2], r[3]); };

// patch ONE skin material; returns the controller for its uniforms
export function faceLayer(material) {
  const T = faceTextures();
  const u = {
    vfA: { value: T.A }, vfB: { value: T.B },
    vfEyeRect: { value: rectVec("eyes") }, vfEyeCell: { value: new THREE.Vector4() }, vfEyeOn: { value: 0 },
    vfMouthRect: { value: rectVec("mouth") }, vfMouthCell: { value: new THREE.Vector4() }, vfMouthOn: { value: 0 },
    vfFxRect: { value: rectVec("fx") },
    vfFxCell: { value: FX_NAMES.map((n) => CELL["fx:" + n].clone()) },
    vfFxAmt: { value: FX_NAMES.map(() => 0) },
  };
  material.onBeforeCompile = (sh) => {
    Object.assign(sh.uniforms, u);
    sh.fragmentShader = sh.fragmentShader
      .replace("#include <common>", "#include <common>\n" + FACE_GLSL)
      .replace("#include <map_fragment>",
        "#ifdef USE_MAP\n vec4 sampledDiffuseColor = texture2D( map, vMapUv );\n sampledDiffuseColor.rgb = vaelFace( sampledDiffuseColor.rgb, vMapUv );\n diffuseColor *= sampledDiffuseColor;\n#endif")
      .replace("#include <emissivemap_fragment>",
        "#ifdef USE_EMISSIVEMAP\n vec4 emissiveColor = texture2D( emissiveMap, vEmissiveMapUv );\n emissiveColor.rgb = vaelFace( emissiveColor.rgb, vEmissiveMapUv );\n totalEmissiveRadiance *= emissiveColor.rgb;\n#endif");
  };
  material.customProgramCacheKey = () => "vaelFace1";
  material.needsUpdate = true;
  const fxAmt = u.vfFxAmt.value;
  return {
    // eyes / mouth: a cell name or null (= the base face); fx: {name: 0..1} targets
    apply(eyes, mouth) {
      const ready = T.ready >= 2;
      u.vfA.value = T.A; u.vfB.value = T.B;
      const ec = ready && eyes ? CELL["eyes:" + eyes] : null;
      const mc = ready && mouth ? CELL["mouth:" + mouth] : null;
      u.vfEyeOn.value = ec ? 1 : 0; if (ec) u.vfEyeCell.value.copy(ec);
      u.vfMouthOn.value = mc ? 1 : 0; if (mc) u.vfMouthCell.value.copy(mc);
      return ready;
    },
    fx(targets, dt) {             // eases each fx toward its target (a blush rises, it doesn't blink on)
      const ready = T.ready >= 2;
      const k = 1 - Math.exp(-dt * 5);
      for (let i = 0; i < FX_NAMES.length; i++) {
        const want = ready && targets ? (targets[FX_NAMES[i]] || 0) : 0;
        fxAmt[i] += (want - fxAmt[i]) * k;
        if (Math.abs(want - fxAmt[i]) < 0.002) fxAmt[i] = want;
      }
    },
  };
}

// ── POP-UPS: the anime marks that float off her head (drawn, no assets) ──
const POP_TEX = {};
function popTexture(kind) {
  if (POP_TEX[kind]) return POP_TEX[kind];
  const c = document.createElement("canvas"); c.width = c.height = 128;
  const g = c.getContext("2d");
  g.lineCap = "round"; g.lineJoin = "round";
  if (kind === "vein") {            // the big red 💢
    g.strokeStyle = "#b3261e"; g.lineWidth = 13;
    for (const [sx, sy] of [[1, 1], [-1, 1], [-1, -1], [1, -1]]) {
      g.beginPath();
      g.moveTo(64 + sx * 12, 64 + sy * 50);
      g.quadraticCurveTo(64 + sx * 15, 64 + sy * 15, 64 + sx * 50, 64 + sy * 12);
      g.stroke();
    }
  } else if (kind === "huff") {     // a puff of steam
    g.fillStyle = "#ffffff"; g.strokeStyle = "#3b3033"; g.lineWidth = 5;
    g.beginPath();
    for (const [x, y, r] of [[40, 70, 24], [66, 56, 28], [90, 72, 22], [64, 82, 22]]) { g.moveTo(x + r, y); g.arc(x, y, r, 0, Math.PI * 2); }
    g.fill();
    g.beginPath();
    for (const [x, y, r] of [[40, 70, 24], [66, 56, 28], [90, 72, 22], [64, 82, 22]]) { g.moveTo(x + r, y); g.arc(x, y, r, 0, Math.PI * 2); }
    g.stroke();
    g.fill();
  } else if (kind === "sparkle") {  // a four-point star
    g.fillStyle = "#ffe07a"; g.strokeStyle = "#8a6520"; g.lineWidth = 4;
    g.beginPath();
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 - Math.PI / 2, r = i % 2 ? 14 : 58;
      g[i ? "lineTo" : "moveTo"](64 + Math.cos(a) * r, 64 + Math.sin(a) * r);
    }
    g.closePath(); g.fill(); g.stroke();
  } else if (kind === "note") {     // a little music note
    g.fillStyle = "#3b3033";
    g.beginPath(); g.ellipse(46, 92, 20, 15, -0.4, 0, Math.PI * 2); g.fill();
    g.fillRect(60, 22, 9, 70);
    g.beginPath(); g.moveTo(64, 22); g.quadraticCurveTo(100, 34, 96, 64); g.quadraticCurveTo(92, 44, 64, 42); g.fill();
  } else if (kind === "heart") {
    g.fillStyle = "#ff6b8a"; g.strokeStyle = "#8b2040"; g.lineWidth = 5;
    g.beginPath(); g.moveTo(64, 104);
    g.bezierCurveTo(10, 66, 22, 18, 64, 42); g.bezierCurveTo(106, 18, 118, 66, 64, 104);
    g.fill(); g.stroke();
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  POP_TEX[kind] = t;
  return t;
}
// where each pop lives, in HEAD-bone space (metres; +x her left, +y up, +z forward)
// and how it moves: pulse (throb in place), drift (rises and fades, repeating)
const POPS = {
  vein:    [{ kind: "vein", at: [0.085, 0.16, 0.05], size: 0.085, anim: "pulse" }],
  veins2:  [{ kind: "vein", at: [0.095, 0.15, 0.04], size: 0.075, anim: "pulse" }, { kind: "vein", at: [-0.11, 0.07, 0.06], size: 0.05, anim: "pulse", phase: 1.3 }],
  huff:    [{ kind: "huff", at: [0.14, 0.03, 0.08], size: 0.10, anim: "puff", dir: [1, -0.2, 0.3] }, { kind: "huff", at: [-0.14, 0.03, 0.08], size: 0.10, anim: "puff", dir: [-1, -0.2, 0.3], phase: 0.5 }],
  sparkle: [{ kind: "sparkle", at: [0.16, 0.12, 0.04], size: 0.05, anim: "twinkle" }, { kind: "sparkle", at: [-0.19, 0.13, 0.03], size: 0.04, anim: "twinkle", phase: 0.9 },
            { kind: "note", at: [0.19, -0.02, 0.03], size: 0.05, anim: "drift", phase: 0.3 }, { kind: "sparkle", at: [-0.12, 0.19, 0.02], size: 0.032, anim: "twinkle", phase: 1.7 }],
  hearts:  [{ kind: "heart", at: [0.15, 0.10, 0.05], size: 0.045, anim: "drift" }, { kind: "heart", at: [-0.16, 0.06, 0.05], size: 0.035, anim: "drift", phase: 1.1 }],
};

// ── MOODS: face + move + props + pops ──────────────────────────────────
// talk: the three flap shapes she talks through IN THIS MOOD — [wide, mid, alt]
// (between flaps the mood's own mouth shows). A furious V'ael talks through her
// teeth, a laughing one keeps laughing; one generic mouth for every mood lost the mood
// the moment she spoke (rig session, 2026-09-24).
// tail: how her tail acts (TAILS below) — it idles in every mood unless told.
export const MOODS = {
  neutral:   { eyes: null, mouth: null, move: "idle" },
  furious:   { eyes: "glare", mouth: "grit", fx: { veins: 1, gloom: 1 }, move: "sleeve", talk: ["yell", "grit", "talk_e"], tail: "lash" },
  seething:  { eyes: "seethe", mouth: "frown", fx: { veins: 1 }, move: "seethe", pops: "vein", noBlink: true, talk: ["talk_e", "frown", "grit"], tail: "twitch" },
  flustered: { eyes: "flustered", mouth: "pout", fx: { blush_hard: 1, tears: 1, sweat: 1 }, move: "point", pops: "huff", talk: ["yell", "talk_o", "pout"], tail: "flick" },
  sulk:      { eyes: "sulk", mouth: "pout", fx: { blush: 0.35 }, move: "sulk", talk: ["talk_e", "pout", "talk_e"], tail: "tap" },
  smug:      { eyes: "smug", mouth: "smug", fx: { choc: 1 }, move: "sundae", props: ["sundae", "spoon"], talk: ["talk_a", "smug", "talk_e"], tail: "swish" },
  yumm:      { eyes: "delight", mouth: "delight", fx: { blush: 1, choc: 1 }, move: "yumm", props: ["sundae", "spoon", "bite"], pops: "hearts", talk: ["delight", "munch", "talk_a"], tail: "wag" },
  bliss:     { eyes: "bliss", mouth: "munch", fx: { blush: 1 }, move: "bliss", props: ["sundae", "spoon"], pops: "sparkle", talk: ["munch", "talk_o", "munch"], tail: "curl" },
  laugh:     { eyes: "laugh", mouth: "laugh", fx: { blush: 0.6 }, move: "facepalm", noBlink: true, talk: ["laugh", "grin", "laugh"], tail: "bounce" },
  scold:     { eyes: "yell", mouth: "yell", move: "beckon", talk: ["yell", "talk_a", "talk_o"], tail: "tap" },
  cackle:    { eyes: "sly", mouth: "grin", move: "cackle", talk: ["laugh", "grin", "grin"], tail: "swish" },
};
const TALK = ["talk_a", "talk_e", "talk_o"];
// ── THE TAIL ACTS — added on top of the walk's own sway (tail1..3, local Euler x/z:
// z swings it side to side, x lifts/curls it). The art keeps her tail hanging
// down her left side, so these stay subtle: a mood flavours it, never flings it.
const P = (x) => Math.max(0, x);
const TAILS = {
  lash:   (t) => [[-0.10, 0, 0.16 * S(t * 5.5)], [0, 0, 0.22 * S(t * 5.5 - 0.9)], [0, 0, 0.30 * S(t * 5.5 - 1.8)]],
  twitch: (t) => [[-0.04, 0, 0], [0, 0, 0], [0.10 * P(S(t * 2.3)) ** 6, 0, 0.30 * P(S(t * 2.3)) ** 6]],
  flick:  (t) => [[-0.06, 0, 0.06 * S(t * 7)], [0, 0, 0.14 * S(t * 7 - 0.6)], [-0.12, 0, 0.26 * S(t * 7 - 1.2)]],
  tap:    (t) => [[0, 0, 0.10], [0, 0, 0.06], [-0.30 * P(S(t * 3.1)) ** 3, 0, 0.10]],
  swish:  (t) => [[0, 0, 0.14 * S(t * 1.3)], [0, 0, 0.20 * S(t * 1.3 - 0.8)], [0, 0, 0.28 * S(t * 1.3 - 1.6)]],
  wag:    (t) => [[-0.05, 0, 0.12 * S(t * 4.2)], [0, 0, 0.18 * S(t * 4.2 - 0.7)], [-0.10, 0, 0.24 * S(t * 4.2 - 1.4)]],
  curl:   (t) => [[-0.06, 0, 0.08 * S(t * 1.1)], [-0.18, 0, 0.10 * S(t * 1.1 - 0.8)], [-0.45, 0, 0.14 * S(t * 1.1 - 1.6)]],
  bounce: (t) => [[-0.06 + 0.05 * S(t * 13), 0, 0.05 * S(t * 3)], [0.05 * S(t * 13 - 0.5), 0, 0.08 * S(t * 3 - 0.7)], [0.06 * S(t * 13 - 1), 0, 0.12 * S(t * 3 - 1.4)]],
};
export const MOOD_NAMES = Object.keys(MOODS);

// ── MOVES: pose functions ───────────────────────────────────────────────
// (t = clock, lt = seconds since this mood began). Each returns:
//   set:   {bone: [x, y, z]} absolute local Euler (three.js XYZ; identity rest)
//   add:   {bone: [x, y, z]} added on top of the idle (keeps her breathing)
//   hands: {L: shape, R: shape}   extra: per-finger extra bends
//   props: {name: [bone, [x, y, z], [rx, ry, rz]]} where a prop sits in a hand
//   shake: amplitude (rad) of a fast tremble over arms, chest and head
//   walk:  0..1 a stroll in place
// Axes (rest pose, arms hanging): upperarm x<0 swings the arm FORWARD/up,
// upperarm z>0 swings her LEFT arm out (−z her right arm); forearm x<0 bends
// the elbow; head x>0 nods down, y>0 turns to her left, z>0 tilts to her right.
const S = Math.sin;
// where a held thing sits in each fist (hand-bone space: fingers hang toward −y at
// rest, the palm faces her midline)
const GRIP = { L: [-0.016, -0.078, 0.004], R: [0.016, -0.078, 0.004] };
const SUNDAE = ["handL", GRIP.L, [0, 0, 0], { upright: true, scale: 1.35 }];
const spoonAt = (aim, roll = 0) => ["handR", [0.012, -0.085, 0.012], [0, roll, 0], { aim }];
const MOVES = {
  idle: () => ({}),
  // 1 — rolling up her left sleeve with her right hand, left fist clenched, shaking
  sleeve: (t) => {
    const tug = Math.max(0, S(t * 5.2)) ** 2;
    return {
      // (upset_10) the elbow DOWN by her waist and a little forward, the fist up at her
      // shoulder; the right arm across her belly, gripping the forearm under the elbow
      ik: {
        L: { to: ["model", [0.30, 1.29 + 0.012 * S(t * 9), 0.11]], pole: [0.6, -1.0, 0.3] },
        R: { to: ["model", [0.135, 1.105 + 0.035 * tug, 0.165]], pole: [-0.3, -1.0, 0.35] },
      },
      aim: { L: { fingers: [0.0, 1, 0.12], palm: [-1, 0, -0.35] }, R: { fingers: [1, 0.15, -0.1], palm: [0, 0.1, -1] } },
      add: { head: [0.10, 0.10, 0.0], chest: [0.03 + 0.02 * S(t * 2.2), 0.10, 0.0], spine: [0.03, 0.06, 0.0] },
      hands: { L: "fist", R: "grip" },
      shake: 0.022,
    };
  },
  // 2 — the same stance frozen: eyes squeezed shut, holding it in (deep slow breaths)
  seethe: (t) => ({
    ik: {
      L: { to: ["model", [0.30, 1.285, 0.11]], pole: [0.6, -1.0, 0.3] },
      R: { to: ["model", [0.135, 1.11, 0.165]], pole: [-0.3, -1.0, 0.35] },
    },
    aim: { L: { fingers: [0.0, 1, 0.12], palm: [-1, 0, -0.35] }, R: { fingers: [1, 0.15, -0.1], palm: [0, 0.1, -1] } },
    add: { head: [0.14, 0.06, 0.03], chest: [-0.03 * (0.5 + 0.5 * S(t * 1.1)), 0.08, 0], neck: [0.04, 0, 0] },
    hands: { L: "fist", R: "grip" },
    shake: 0.008,
  }),
  // 3 — flustered: a finger jabbed up at you by her shoulder, the other fist down at her side
  point: (t) => {
    const jab = Math.max(0, S(t * 3.4)) ** 3;
    return {
      // (upset_8) the hand OUT past her left shoulder, the finger jabbed at you, tipped up
      // (upset_8, checked side by side) the hand at her CHEST, out in front, elbow down;
      // the index up and at you, the palm toward her midline
      ik: { L: { to: ["model", [0.22, 1.19 + 0.012 * jab, 0.29 + 0.05 * jab]], pole: [0.5, -1.0, -0.1] } },
      aim: { L: { fingers: [-0.05, 0.62, 0.78], palm: [-1, 0, 0.1] } },
      set: { upperarmR: [0.06, 0.0, -0.10], forearmR: [-0.30, 0.0, 0.0] },
      add: { head: [0.10, 0.10, -0.06], chest: [0.04 * jab, 0.10, 0], spine: [0.04, 0.05, 0] },
      hands: { L: "point", R: "fist" },
      shake: 0.005,
    };
  },
  // 4 — arms crossed under her bust, chin up, looking away
  sulk: (t) => ({
    ik: {
      L: { to: ["model", [-0.07, 1.145, 0.20]], pole: [0.7, -0.6, -0.2] },
      R: { to: ["model", [0.08, 1.165, 0.235]], pole: [-0.7, -0.6, -0.2] },
    },
    aim: { L: { fingers: [-0.15, -0.25, -1], palm: [0.6, 0, 0.4] }, R: { fingers: [0.15, -0.25, -1], palm: [-0.6, 0, 0.4] } },
    add: { head: [-0.07, -0.08, 0.08], hips: [0, 0.10, 0.03], chest: [0.02 * S(t * 0.9), -0.06, 0] },
    hands: { L: "grip", R: "grip" },
  }),
  // 5 — the sundae held up in her left hand, the spoon dangling from her right
  sundae: (t) => ({
    ik: {
      L: { to: ["model", [0.30, 1.21, 0.17]], pole: [0.6, -0.9, -0.1] },
      R: { to: ["model", [-0.215, 0.93, 0.08]], pole: [-0.4, -0.3, -0.8] },
    },
    set: { handL: [0.0, 0.0, 0.0], handR: [0.2, 0.0, 0.0] },
    add: { head: [0.02, 0.12, -0.10 + 0.03 * S(t * 0.8)] },
    hands: { L: "grip", R: "pinch" },
    props: { sundae: SUNDAE, spoon: ["handR", [0.012, -0.085, 0.012], [0.5, 0, 0], { aim: ["handR", [0.0, -0.3, 0.12]] }] },
  }),
  // 6 — YUMM: the spoon up at her mouth, a little happy bounce
  yumm: (t) => ({
    ik: {
      L: { to: ["model", [0.30, 1.21, 0.17]], pole: [0.6, -0.9, -0.1] },
      R: { to: ["head", [-0.135, -0.075, 0.16]], pole: [-0.45, -1.0, 0.1] },
    },
    // the right hand HOLDS the spoon palm-in, fingers up-and-across, wrist near straight (it was palm-UP, the wrist cocked ~70 deg backwards; caP'n 2026-10-01)
    aim: { R: { fingers: [0.6, 0.75, -0.1], palm: [0.8, -0.4, -0.35], space: "head" } },
    add: { head: [-0.04, 0.0, -0.06], chest: [0.02 * S(t * 6), 0, 0] },
    hands: { L: "grip", R: "pinch" },
    props: { sundae: SUNDAE, spoon: spoonAt(["head", [0.005, 0.012, 0.17]]) },
    bounce: 0.010,
  }),
  // 7 — bliss: the spoon IN her mouth, strolling along, head tipped
  bliss: (t) => ({
    ik: {
      L: { to: ["model", [0.30, 1.19, 0.17]], pole: [0.6, -0.9, -0.1] },
      // (sweettreat_3) the spoon comes in from her right side, the handle out
      R: { to: ["head", [-0.15, -0.02, 0.15]], pole: [-0.45, -1.0, 0.1] },
    },
    aim: { R: { fingers: [0.6, 0.75, 0.12], palm: [0.8, -0.4, -0.35], space: "head" } },
    add: { head: [0.10, 0.12, -0.14] },
    hands: { L: "grip", R: "pinch" },
    props: { sundae: SUNDAE, spoon: spoonAt(["head", [0.0, 0.04, 0.10]]) },
    walk: 0.55,
  }),
  // 8 — hand on her forehead, head back, laughing (the other hand open, palm up)
  facepalm: (t) => {
    const ha = S(t * 13) * 0.5 + 0.5;
    return {
      ik: {
        // (sillyme_2) the palm flat on her FOREHEAD, above the eyes, fingers up into her
        // fringe, the elbow up and out — her laughing eyes must show under it
        R: { to: ["head", [-0.12, 0.13, 0.15]], pole: [-0.8, 0.3, -0.2] },
        L: { to: ["model", [0.33, 1.17, 0.20]], pole: [0.5, -1.0, -0.2] },
      },
      aim: { R: { fingers: [0.9, 0.35, 0.05], palm: [0.05, -0.15, -1], space: "head" }, L: { fingers: [0.5, 0.1, 1], palm: [0, 1, 0] } },
      add: { head: [-0.14, -0.06, 0.08], chest: [-0.05 + 0.03 * ha, 0, 0], spine: [-0.03, 0, 0] },
      hands: { L: "open", R: "soft" },
      shake: 0.004,
    };
  },
  // 9 — scolding: leaning in, beckoning you over (fingers flicking)
  beckon: (t) => {
    const f = Math.max(0, S(t * 6.0));
    return {
      // (heyyou_2) the hand up in front of her chest, palm toward HER, fingers curling in
      ik: { R: { to: ["model", [-0.10, 1.22, 0.27]], pole: [-0.6, -0.8, 0.0] } },
      aim: { R: { fingers: [0.1, 1, 0.1], palm: [0.1, 0.1, -1] } },
      set: { upperarmL: [0.04, 0.0, 0.08], forearmL: [-0.25, 0.0, 0.0] },
      add: { head: [0.10, -0.06, 0.04], spine: [0.08, 0, 0], chest: [0.04, -0.10, 0] },
      hands: { L: "relaxed", R: "beckon" },
      extra: { R: { index: [30 * f, 60 * f, 45 * f], middle: [15 * f, 30 * f, 25 * f] } },
    };
  },
  // 10 — the cackle: hand on her forehead, the other hand up and open, head tipped
  cackle: (t) => {
    const ha = S(t * 11) * 0.5 + 0.5;
    return {
      ik: {
        // (sillyme_1) the palm pressed on TOP of her hair, the head tipped toward it
        R: { to: ["head", [-0.09, 0.30, 0.07]], pole: [-0.8, 0.5, -0.1] },
        L: { to: ["model", [0.27, 1.30, 0.14]], pole: [0.6, -0.8, -0.2] },
      },
      aim: { R: { fingers: [0.85, 0.2, -0.25], palm: [0.1, -1, 0.25], space: "head" }, L: { fingers: [0.35, 1, 0.1], palm: [0.15, 0.2, 1] } },
      add: { head: [-0.05, -0.18, 0.17], chest: [-0.03 + 0.025 * ha, 0, 0] },
      hands: { L: "claw", R: "soft" },
      shake: 0.003,
    };
  },
};

// ── TWO-BONE IK: the arms reach for a point ─────────────────────────────
// A move may say  ik: { R: { to: ["head", [x, y, z]], pole: [x, y, z] } }  — the
// hand (wrist) goes to a point in a BONE's space (it follows that bone: a hand on
// her forehead stays there while she laughs), or ["model", [x, y, z]] in her own
// space. pole = where the elbow should point (model space). The solve gives the
// upper arm and forearm rotations, which then ease in like any other pose.
const V3 = () => new THREE.Vector3();
const _S = V3(), _T = V3(), _D = V3(), _N = V3(), _A = V3(), _F = V3(), _Bv = V3(), _C = V3(), _E = V3(), _P = V3(), _U = V3();
const _q1 = new THREE.Quaternion(), _q2 = new THREE.Quaternion(), _q3 = new THREE.Quaternion();
const _m1 = new THREE.Matrix4(), _m2 = new THREE.Matrix4(), _inv = new THREE.Matrix4();
const _eul = new THREE.Euler();
const _st = [0, 0, 0];
function restArm(B, k, toModel) {
  const S0 = toModel(B["upperarm" + k], V3()), E0 = toModel(B["forearm" + k], V3()), H0 = toModel(B["hand" + k], V3());
  const u0 = E0.clone().sub(S0), f0 = H0.clone().sub(E0);
  const L1 = u0.length(), L2 = f0.length();
  u0.normalize(); f0.normalize();
  const b0 = V3().crossVectors(u0, new THREE.Vector3(0, 0, 1)).normalize();   // the elbow's hinge at rest
  const c0 = V3().crossVectors(u0, b0);
  const Rrest = new THREE.Matrix4().makeBasis(u0, b0, c0);
  return { L1, L2, f0, RrestT: Rrest.clone().transpose(), H0: H0.clone() };
}

// the bones a move may touch (everything else stays the idle's)
const ACT_BONES = ["hips", "spine", "chest", "neck", "head", "upperarmL", "forearmL", "handL", "upperarmR", "forearmR", "handR",
  "thighL", "shinL", "footL", "thighR", "shinR", "footR"];
const LEGS = { thighL: 1, shinL: 1, footL: 1, thighR: 1, shinR: 1, footR: 1 };
// THE STANCE: every picture has her mid-stride toward you — her RIGHT foot a step
// forward, that knee bent and turned in (a catwalk cross). A mood stands like that unless its move
// says otherwise (stance: null) or walks.
// (rig session: the old cross hid her back boot entirely from the front — the art
// shows BOTH boots, the front one a half-step across; the back leg a little out)
const STANCE = { thighR: [-0.24, 0.0, 0.03], shinR: [0.26, 0.0, 0.0], footR: [-0.06, 0.0, 0.0],
  thighL: [0.10, 0.0, 0.035], shinL: [0.10, 0.0, 0.0], footL: [-0.02, 0.0, 0.0], hips: [0.0, 0.07, -0.02] };
const PROP_SETS = {
  sundae: ["vael_prop_glass", "vael_prop_vanilla", "vael_prop_choc", "vael_prop_syrup", "vael_prop_cream", "vael_prop_cherry"],
  spoon: ["vael_prop_spoon"],
  bite: ["vael_prop_bite"],
};

// ── THE ACTOR: one per acting body ──────────────────────────────────────
export function makeActor(root, B, loadProps) {
  // props: one group per set (sundae, spoon, bite) we can hand to a bone. The meshes
  // arrive from vael_props.glb when it loads; until then the groups are empty.
  const props = {};
  for (const name of Object.keys(PROP_SETS)) {
    const g = new THREE.Group(); g.name = "prop_" + name; g.visible = false;
    props[name] = g;
  }
  props.bite.userData.follow = "spoon";         // the bite rides the spoon
  if (loadProps) loadProps((scene) => {
    for (const [name, meshes] of Object.entries(PROP_SETS)) {
      for (const mn of meshes) {
        for (const suffix of ["", "_ink"]) {
          const o = scene.getObjectByName(mn + suffix);
          if (o) { o.removeFromParent(); o.position.set(0, 0, 0); o.rotation.set(0, 0, 0); o.scale.set(1, 1, 1); props[name].add(o); }
        }
      }
    }
  });
  let face = null;
  root.traverse((o) => { if (o.isMesh && !face && o.name === "vael_skin") face = faceLayer(o.material); });
  // pops
  const popRoot = new THREE.Group(); popRoot.name = "vael_pops";
  if (B.head) B.head.add(popRoot);
  const popSprites = [];
  let popKey = null;
  const setPops = (key) => {
    if (key === popKey) return; popKey = key;
    for (const s of popSprites) { s.removeFromParent(); s.material.dispose(); }
    popSprites.length = 0;
    for (const p of (key && POPS[key]) || []) {
      // drawn on top of her (a mark floats over the hair, like the art's)
      const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: popTexture(p.kind), transparent: true, depthWrite: false, depthTest: false }));
      s.renderOrder = 10;
      s.userData = p; s.position.set(...p.at); s.scale.setScalar(p.size);
      popRoot.add(s); popSprites.push(s);
    }
  };
  const cur = {};                 // smoothed rotation per acting bone (quaternions: no Euler wrap)
  for (const n of ACT_BONES) if (B[n]) cur[n] = B[n].quaternion.clone();
  const tgt = {}, done = {};
  const TRUNK = ["hips", "spine", "chest", "neck", "head"], ARM_L = ["upperarmL", "forearmL", "handL"];
  const TRUNK_I = { hips: 1, spine: 1, chest: 1, neck: 1, head: 1 };
  for (const n of ACT_BONES) tgt[n] = new THREE.Quaternion();
  // model space = her own space (the root's), whatever the scene does to the root
  const toModel = (o, out) => { o.getWorldPosition(out); return out.applyMatrix4(_inv); };
  root.updateMatrixWorld(true);
  _inv.copy(root.matrixWorld).invert();
  const REST = { L: restArm(B, "L", toModel), R: restArm(B, "R", toModel) };
  const lastHand = { L: REST.L.H0.clone(), R: REST.R.H0.clone() };
  const HOME = { L: { to: ["model", REST.L.H0.toArray()], pole: [0.25, -0.2, -1] }, R: { to: ["model", REST.R.H0.toArray()], pole: [-0.25, -0.2, -1] } };
  // (a hanging arm points DOWN, so an elbow pole that is also mostly down is nearly
  // parallel to it and the bend direction is noise — that was the hands-on-hips flare;
  // at rest the elbow points BACK)
  const ikT = { L: V3(), R: V3() }, ikOn = { L: false, R: false };
  // THE HAND TRAVELS (rig session 2026-09-24). A newly-reaching hand starts from where
  // the wrist IS and arcs to its goal in her own space (out in front of her, never
  // through her hip), with slow-in/slow-out; a hand let go travels back to its rest
  // spot the same way before the idle takes it. Before this, the IK goal jumped and
  // only the joint angles eased — every arm flung out wide for a frame or three.
  const trav = { L: { u: 1, dur: 0.4, from: V3(), key: null, pole0: new THREE.Vector3(0.25, -0.2, -1).normalize(), pole: new THREE.Vector3(0.25, -0.2, -1).normalize(), e: 1 },
                 R: { u: 1, dur: 0.4, from: V3(), key: null, pole0: new THREE.Vector3(-0.25, -0.2, -1).normalize(), pole: new THREE.Vector3(-0.25, -0.2, -1).normalize(), e: 1 } };
  const _G = V3(), _M = V3();
  // a quick gesture: a short slow-in, most of the way fast, a long soft settle
  const ease = (u) => 0.35 * u * u * (3 - 2 * u) + 0.65 * (1 - (1 - u) ** 3);
  const modelQuat = (o, out) => {           // an object's rotation in model space
    o.getWorldQuaternion(out);
    root.getWorldQuaternion(_q3); _q3.invert();
    return out.premultiply(_q3);
  };
  // AIM A HAND: aim = { fingers: [x, y, z], palm: [x, y, z], space: "model" | bone }
  // the fingers' direction and the way the palm faces, in her space (or a bone's).
  // At rest her fingers hang (−y) and her palms face her midline.
  const aimHand = (k, a) => {
    const hb = B["hand" + k]; if (!hb) return;
    const sp = a.space && a.space !== "model" ? B[a.space] : null;
    _A.set(...a.fingers); _N.set(...a.palm);
    if (sp) { modelQuat(sp, _q2); _A.applyQuaternion(_q2); _N.applyQuaternion(_q2); }
    _A.normalize();
    _N.addScaledVector(_A, -_N.dot(_A)).normalize();
    _C.crossVectors(_A, _N);
    _m1.makeBasis(_A, _N, _C);
    // the rest basis: fingers (0,−1,0), palm toward the midline
    _F.set(0, -1, 0); _Bv.set(k === "L" ? -1 : 1, 0, 0); _U.crossVectors(_F, _Bv);
    _m2.makeBasis(_F, _Bv, _U).transpose();
    _q1.setFromRotationMatrix(_m1.multiply(_m2));                                     // hand, model space
    modelQuat(hb.parent, _q2);
    tgt["hand" + k].copy(_q2).invert().multiply(_q1);
  };
  // solve one arm; writes the TARGET quaternions of its upper arm and forearm
  const solve = (k, spec, dt, fresh, key) => {
    const R0 = REST[k];
    const up = B["upperarm" + k], fo = B["forearm" + k];
    if (!up || !fo) return;
    const [bn, off] = spec.to;
    if (bn === "model") _T.set(off[0], off[1], off[2]);
    else { const b = B[bn]; if (!b) return; _T.set(off[0], off[1], off[2]); b.localToWorld(_T); _T.applyMatrix4(_inv); }
    // the hand TRAVELS to the goal (see trav above), then follows it softly
    const tr = trav[k];
    if (fresh || key !== tr.key) {
      // from where the hand WAS last frame (the bones now hold this frame's idle)
      tr.from.copy(lastHand[k]); tr.key = key; tr.u = 0;
      tr.pole0.copy(tr.pole);                  // the elbow swings from where it pointed, too
      tr.dur = Math.min(0.48, Math.max(0.24, 0.18 + 0.7 * tr.from.distanceTo(_T)));
    }
    if (tr.u < 1) {
      tr.u = Math.min(1, tr.u + dt / tr.dur);
      const e = ease(tr.u);
      // a quadratic arc: the control point sits out in FRONT of the straight line
      // a cubic arc that leaves FORWARD, the way a forearm swings up from the elbow
      // (a hand that first rises along her side lands on her hip on the way — that was
      // the hands-on-hips in-between every mood shared), and arrives from the front
      const dd = tr.from.distanceTo(_T), f = 1 - e;
      _M.copy(tr.from); _M.z += 0.50 * dd + 0.05; _M.y += 0.10 * dd;            // leave forward…
      _M.x *= 0.88;                                                              // …and a little IN, not out to the hip
      // arrive from the front; a hand going up to her head comes in from ABOVE the
      // face (from below, it swept across her eyes and read as a gasp)
      const up = _T.y > 1.42;
      _C.copy(_T); _C.z += (up ? 0.06 : 0.12) * dd; _C.y += (up ? 0.10 : -0.04) * dd;
      _G.copy(tr.from).multiplyScalar(f * f * f).addScaledVector(_M, 3 * f * f * e)
        .addScaledVector(_C, 3 * f * e * e).addScaledVector(_T, e * e * e);
      ikT[k].copy(_G);
      tr.e = e;
    } else { ikT[k].lerp(_T, 1 - Math.exp(-dt * 14)); tr.e = 1; }
    toModel(up, _S);
    _D.copy(ikT[k]).sub(_S);
    let d = _D.length(); _D.normalize();
    d = Math.min(Math.max(d, Math.abs(R0.L1 - R0.L2) + 0.01), R0.L1 + R0.L2 - 0.004);
    const cosA = Math.min(1, Math.max(-1, (R0.L1 * R0.L1 + d * d - R0.L2 * R0.L2) / (2 * R0.L1 * d)));
    const sinA = Math.sqrt(1 - cosA * cosA);
    const pole = spec.pole || [k === "L" ? 0.5 : -0.5, -0.6, -0.5];
    _P.set(pole[0], pole[1], pole[2]).normalize();
    // on the way the elbow turns gradually from its old aim (a lifted hand leads with
    // a LOW elbow; the elbow only swings out as the hand arrives)
    // (letting go, the elbow drops back EARLY, or the arm sweeps out sideways on its way down)
    if (tr.e < 1) _P.lerpVectors(tr.pole0, _P, key === "release" ? Math.sqrt(tr.e) : tr.e * tr.e * tr.e).normalize();
    tr.pole.copy(_P);
    _N.copy(_P).addScaledVector(_D, -_P.dot(_D));
    if (_N.lengthSq() < 1e-8) _N.set(0, 0, -1);
    _N.normalize();
    _A.copy(_D).multiplyScalar(cosA).addScaledVector(_N, sinA).normalize();        // the upper arm
    _E.copy(_S).addScaledVector(_A, R0.L1);
    _F.copy(_S).addScaledVector(_D, d).sub(_E).normalize();                          // the forearm
    _Bv.crossVectors(_A, _F);
    if (_Bv.lengthSq() < 1e-8) _Bv.crossVectors(_A, _U.set(0, 0, 1));
    _Bv.normalize();
    _C.crossVectors(_A, _Bv);
    _m1.makeBasis(_A, _Bv, _C).multiply(R0.RrestT);
    _q1.setFromRotationMatrix(_m1);                                                   // upper arm, model space
    modelQuat(up.parent, _q2);                                                        // the chest, model space
    tgt["upperarm" + k].copy(_q2).invert().multiply(_q1);
    // the forearm: turn its rest direction (as carried by the upper arm) onto _F
    _U.copy(R0.f0).applyQuaternion(_q1);
    _q2.setFromUnitVectors(_U, _F);                                                   // model-space turn
    tgt["forearm" + k].copy(_q1).invert().multiply(_q2).multiply(_q1);
  };
  const hand = { L: { from: "relaxed", to: "relaxed", t: 1 }, R: { from: "relaxed", to: "relaxed", t: 1 } };
  let mood = null, since = 0, blinkAt = 3.5, blinkT = -1, talkPh = 0, walkAmt = 0, walkPh = 0;
  // THE SWAP BLINK: when the mood changes her face changes BEHIND a blink (the old
  // face closes, the new one opens) instead of popping while the body is still on its
  // way — the anime cut. faceM is the mood her face is showing.
  let faceM = null, swapT = -1;
  // the tail's mood layer, eased (a mood flavours the walk's own sway)
  const tailB = ["tail1", "tail2", "tail3"], tailCur = [V3(), V3(), V3()];
  // THE POSE LAW: a body whose pose never re-sets the tail (the desktop's) must not
  // collect our layer frame on frame — so if the bone still holds what we wrote last
  // frame, the base is that minus our last layer; otherwise its pose wrote it fresh
  const tailW = [V3(), V3(), V3()], tailA = [V3(), V3(), V3()];
  // props grow into the hand instead of popping in whole
  const propAge = {};
  // THE DREADS SWING (rig session 2026-09-24): vael.glb carries two joints per side
  // (dreadL1/L2, dreadR1/R2, identity rest under the head). Each chain is a damped
  // spring toward where "gravity" would hang it — real gravity minus how her head is
  // accelerating — so a laugh bounces them, a shake jitters them and a head tilt lets
  // them hang. A glb without the joints just skips this.
  const DR = [["dreadL1", "dreadL2"], ["dreadR1", "dreadR2"]].map(([a, b]) => ({
    b1: B[a], b2: B[b], p: V3(), v: V3(), init: false,
    a1: new THREE.Vector2(), w1: new THREE.Vector2(), a2: new THREE.Vector2(), w2: new THREE.Vector2(),
  }));
  const _dp = V3(), _dv = V3(), _dg = V3(), _hq = new THREE.Quaternion();
  const mid = {};                 // the trunk's second easing stage (slow-in AND slow-out)
  for (const n of ACT_BONES) mid[n] = cur[n].clone();

  return {
    get walk() { return { amt: walkAmt, ph: walkPh }; },
    // called AFTER the idle/walk pose has been written into the bones
    update(t, dt, ctl) {
      const name = (ctl && ctl.mood) || "neutral";
      const M = MOODS[name] || MOODS.neutral;
      if (name !== mood) {
        if (mood !== null && !(ctl && ctl.blink === false)) { swapT = t; blinkT = t; }
        else faceM = M;
        mood = name; since = t;
      }
      if (swapT >= 0 && t - swapT >= 0.05) { faceM = M; swapT = -1; }
      if (!faceM) faceM = M;
      const lt = t - since;
      const mv = (MOVES[M.move] || MOVES.idle)(t, lt) || {};
      // the stroll (bliss) is driven here, the game's own walk only by ctl.speed
      walkAmt += ((mv.walk || 0) - walkAmt) * Math.min(1, dt * 3);
      walkPh += dt * 5.5 * walkAmt;
      // ── body: target = idle (+add) or set; IK arms solve on top; all eased
      const k = 1 - Math.exp(-dt * (lt < 0.6 ? 7 : 14));
      for (const n of ACT_BONES) {
        const b = B[n]; if (!b) continue;
        const idle = b.rotation;
        const s = mv.set && mv.set[n];
        let a = mv.add && mv.add[n];
        const st = name !== "neutral" && !mv.walk && mv.stance !== null ? (mv.stance || STANCE)[n] : null;
        if (st) { a = a ? (_st[0] = a[0] + st[0], _st[1] = a[1] + st[1], _st[2] = a[2] + st[2], _st) : st; }
        // in a mood her idle head-turn calms to a third (a glare shouldn't wander)
        const keep = n === "head" && name !== "neutral" ? 0.35 : 1;
        _eul.set(s ? s[0] : idle.x * keep + (a ? a[0] : 0),
                 s ? s[1] : idle.y * keep + (a ? a[1] : 0),
                 s ? s[2] : idle.z * keep + (a ? a[2] : 0));
        tgt[n].setFromEuler(_eul);
      }
      const shake = (n) => {
        if (!mv.shake || n === "hips" || n === "spine" || LEGS[n]) return null;
        _eul.set(mv.shake * (S(t * 41 + n.length) * 0.6 + S(t * 67 + n.length * 3) * 0.4), 0, mv.shake * S(t * 53 + n.length * 7));
        return _q3.setFromEuler(_eul);
      };
      const k2s = 1 - Math.exp(-dt * (lt < 0.8 ? 10 : 16));
      const kHead = 1 - Math.exp(-dt * (lt < 0.8 ? 7.5 : 12));
      const write = (n) => {
        const b = B[n]; if (!b) return;
        if (TRUNK_I[n] || LEGS[n]) {             // S-curve: mid chases the target, cur chases mid
          const kk = n === "head" || n === "neck" ? kHead : k2s;
          mid[n].slerp(tgt[n], kk); cur[n].slerp(mid[n], kk);
        } else {
          // an IK arm already travels smoothly: its joints follow the solve closely
          // (easing the ANGLES too is what swung the arms wide on the way)
          const side = n.endsWith("L") ? "L" : "R";
          cur[n].slerp(tgt[n], ikOn[side] && n !== "hand" + side ? 1 - Math.exp(-dt * 30) : k);
          mid[n].copy(cur[n]);
        }
        b.quaternion.copy(cur[n]);
        const j = shake(n); if (j) b.quaternion.multiply(j);
      };
      for (const n of ACT_BONES) done[n] = false;
      for (const n of TRUNK) { write(n); done[n] = true; }
      const ikAny = mv.ik || ikOn.L || ikOn.R;
      if (ikAny) {
        root.updateMatrixWorld(true);
        _inv.copy(root.matrixWorld).invert();
        for (const side of ["L", "R"]) {          // L first: a right hand may hold the left arm
          let spec = mv.ik && mv.ik[side];
          let key = name;
          if (!spec) {
            if (!ikOn[side]) continue;
            if (mv.set && mv.set["upperarm" + side]) { ikOn[side] = false; continue; }
            // LET GO: the hand travels home to its rest spot, then the idle has it
            const tr = trav[side];
            if (tr.key === "release" && tr.u >= 1) { ikOn[side] = false; continue; }
            spec = HOME[side]; key = "release";
          }
          // the right hand may hold the LEFT arm: finish the left arm first (not an
          // aimed left hand — that is placed last, or it gets pulled two ways a frame)
          if (side === "R" && mv.ik && mv.ik.L) {
            for (const n of ARM_L) if (!(n === "handL" && mv.aim && mv.aim.L)) { write(n); done[n] = true; }
            root.updateMatrixWorld(true);
          }
          solve(side, spec, dt, !ikOn[side], key);
          ikOn[side] = true;
        }
      }
      // hands with an AIM are placed last, in the arm's final frame
      for (const n of ACT_BONES) if (!done[n] && !(mv.aim && ((n === "handL" && mv.aim.L) || (n === "handR" && mv.aim.R)))) write(n);
      if (mv.aim) {
        root.updateMatrixWorld(true);
        _inv.copy(root.matrixWorld).invert();
        for (const side of ["L", "R"]) {
          const a = mv.aim[side];
          if (!a) continue;
          aimHand(side, a);
          write("hand" + side);
        }
      }
      if (mv.bounce && B.hips) B.hips.position.y += Math.abs(S(t * 7)) * mv.bounce;
      // where each hand ended up (a hand that starts to travel next frame starts HERE)
      root.updateMatrixWorld(true);
      _inv.copy(root.matrixWorld).invert();
      toModel(B.handL, lastHand.L); toModel(B.handR, lastHand.R);
      // ── the dreads
      const hdt = Math.max(dt, 1e-3);
      for (const c of DR) {
        // (a body whose rig runs its hair as chains — ieRig.js, the same solver as the tail and
        // skirt — leaves the dreads to it: one solver per bone)
        if (!c.b1 || !c.b2 || c.b1.userData.rigChain) continue;
        c.b1.parent.getWorldQuaternion(_hq); _hq.invert();
        c.b1.getWorldPosition(_dp);
        if (!c.init) { c.p.copy(_dp); c.v.set(0, 0, 0); c.init = true; }
        _dv.copy(_dp).sub(c.p).divideScalar(hdt);                     // pivot velocity
        _dg.copy(_dv).sub(c.v).divideScalar(hdt);                     // …and acceleration
        c.p.copy(_dp); c.v.copy(_dv);
        if (_dg.length() > 60) _dg.setLength(60);
        _dg.multiplyScalar(-2.6); _dg.y -= 9.8;                       // felt gravity = g − a (a cartoon's 2.6×)
        _dg.applyQuaternion(_hq).normalize();                        // in the head's frame
        // the angles that turn "straight down the head" onto the felt gravity (70%: hair has spring)
        const tz = 0.85 * Math.atan2(_dg.x, -_dg.y), tx = 0.85 * Math.atan2(-_dg.z, -_dg.y);
        const K = 150, C = 7.5, K2 = 240, C2 = 10;
        c.w1.x += (K * (tx - c.a1.x) - C * c.w1.x) * hdt; c.w1.y += (K * (tz - c.a1.y) - C * c.w1.y) * hdt;
        c.a1.x += c.w1.x * hdt; c.a1.y += c.w1.y * hdt;
        c.w2.x += (K2 * (c.a1.x - c.a2.x) - C2 * c.w2.x) * hdt; c.w2.y += (K2 * (c.a1.y - c.a2.y) - C2 * c.w2.y) * hdt;
        c.a2.x += c.w2.x * hdt; c.a2.y += c.w2.y * hdt;
        const cl = (v) => Math.max(-0.45, Math.min(0.45, v));
        c.a1.set(cl(c.a1.x), cl(c.a1.y)); c.a2.set(cl(c.a2.x), cl(c.a2.y));
        c.b1.rotation.set(c.a1.x * 0.6, 0, c.a1.y * 0.6);
        c.b2.rotation.set((c.a2.x - c.a1.x) * 0.6 + c.a2.x * 0.4, 0, (c.a2.y - c.a1.y) * 0.6 + c.a2.y * 0.4);
      }
      // ── fingers
      for (const k2 of ["L", "R"]) {
        // hands: the scene's ctl.hands > a playing clip's > the mood's (Willie's rule)
        const want = (ctl && ctl.hands && ctl.hands[k2]) || (ctl && ctl.clipHands && ctl.clipHands[k2]) || (mv.hands && mv.hands[k2]) || "relaxed";
        const h = hand[k2];
        if (want !== h.to) { h.from = h.t >= 0.5 ? h.to : h.from; h.to = want; h.t = 0; }
        h.t = Math.min(1, h.t + dt * 5);
        const e = h.t * h.t * (3 - 2 * h.t);
        // per-finger extra bends: a clip's (piano keys, a beckon) or the mood's
        const cfg = ctl && ctl.clipFingers;
        poseHand(B, k2, h.from, h.to, e, (cfg && cfg[k2]) || (mv.extra && mv.extra[k2]));
      }
      // ── props
      let placed = false;
      const want = M.props || [];
      for (const [pn, g] of Object.entries(props)) {
        // a prop arrives once the hand is most of the way there, and LEAVES by
        // shrinking where it was held (not vanishing from a still-gripping hand)
        const on = want.includes(pn) && lt > 0.26;
        let grow;
        if (on) {
          propAge[pn] = Math.min(0.24, (propAge[pn] || 0) + dt);
          const pa = propAge[pn] / 0.24;
          grow = pa >= 1 ? 1 : 1 + 2.2 * (pa - 1) ** 3 + 1.2 * (pa - 1) ** 2;   // ease-out-back
        } else {
          propAge[pn] = Math.max(0, (propAge[pn] || 0) - dt * 0.8);
          const pa = propAge[pn] / 0.24;
          grow = pa * pa;
        }
        g.visible = grow > 0.002;
        if (!g.visible) continue;
        const follow = g.userData.follow;
        let spec = on && mv.props && mv.props[follow || pn];
        if (spec) g.userData.spec = spec; else spec = g.userData.spec;
        if (!spec) { g.visible = false; continue; }
        const bone = B[spec[0]];
        if (g.parent !== bone && bone) bone.add(g);
        g.position.set(...spec[1]);
        g.rotation.set(...spec[2]);
        const how = spec[3];
        g.scale.setScalar(((how && how.scale) || 1) * Math.max(0.001, grow));
        if (how) {
          // upright: the prop keeps its own +y pointing UP in her space (a held glass
          // doesn't tip with the wrist); aim: its +y points at a spot on a bone (the
          // spoon's bowl at her mouth). spec[2] then turns it about that axis.
          if (!placed) { root.updateMatrixWorld(true); placed = true; }
          if (how.aim) {
            const tb = B[how.aim[0]];
            _T.set(...how.aim[1]); tb.localToWorld(_T);
            g.getWorldPosition(_S);
            _D.copy(_T).sub(_S).normalize();
            _q1.setFromUnitVectors(_U.set(0, 1, 0), _D);
          } else {
            root.getWorldQuaternion(_q1);
          }
          _q3.setFromEuler(_eul.set(...spec[2])); _q1.multiply(_q3);
          bone.getWorldQuaternion(_q2);
          g.quaternion.copy(_q2.invert().multiply(_q1));
        }
      }
      // ── face
      if (face) {
        const FM = faceM;
        const cf = ctl && ctl.clipFace;
        let eyes = ctl && ctl.eyes !== undefined ? ctl.eyes : cf && cf.eyes !== undefined ? cf.eyes : FM.eyes;
        let mouth = ctl && ctl.mouth !== undefined ? ctl.mouth : cf && cf.mouth !== undefined ? cf.mouth : FM.mouth;
        // the blink: every 2.5–5.5 s, 0.14 s long, a half-lid frame either side
        const swapping = blinkT >= 0 && t - blinkT < 0.16 && since === blinkT;
        const shut = !swapping && (eyes === "seethe" || eyes === "laugh" || eyes === "blink" || FM.noBlink || (ctl && ctl.blink === false));
        // timed by the CLOCK, not summed frame times: a slow machine's long
        // frames must not stretch one blink across a second
        if (blinkT < 0 && t > blinkAt) { blinkT = t; }
        if (blinkT >= 0) {
          const bt = t - blinkT;
          if (!shut) eyes = bt < 0.04 || bt > 0.11 ? "half" : "blink";
          if (bt > 0.15) { blinkT = -1; blinkAt = t + 2.5 + ((t * 7.31) % 3); }
        }
        if (ctl && ctl.talking) {
          talkPh += dt * 11;
          const w = S(talkPh) + 0.5 * S(talkPh * 2.3 + 1);
          const tk = FM.talk || TALK;
          mouth = w > 0.7 ? tk[0] : w > 0.1 ? tk[1] : w > -0.5 ? mouth || null : tk[2];
        }
        face.apply(eyes, mouth);
        const cfx = cf && cf.fx;
        face.fx(ctl && ctl.fx ? { ...FM.fx, ...cfx, ...ctl.fx } : cfx ? { ...FM.fx, ...cfx } : FM.fx, dt);
      }
      // ── pops
      setPops(ctl && ctl.pops !== undefined ? ctl.pops : (faceM && faceM.pops) || null);
      // ── the tail: the mood's layer on top of the walk's sway, eased in and out
      const tf = TAILS[M.tail];
      const tv = tf ? tf(t) : null;
      const kt = 1 - Math.exp(-dt * 4);
      for (let i = 0; i < 3; i++) {
        const b = B[tailB[i]]; if (!b) continue;
        const c = tailCur[i];
        c.x += ((tv ? tv[i][0] : 0) - c.x) * kt; c.z += ((tv ? tv[i][2] : 0) - c.z) * kt;
        const w = tailW[i], a = tailA[i];
        const stale = b.rotation.x === w.x && b.rotation.z === w.z;
        const bx = stale ? w.x - a.x : b.rotation.x, bz = stale ? w.z - a.z : b.rotation.z;
        b.rotation.x = bx + c.x; b.rotation.z = bz + c.z;
        w.set(b.rotation.x, 0, b.rotation.z); a.set(c.x, 0, c.z);
      }
      for (const s of popSprites) {
        const p = s.userData, ph = t + (p.phase || 0);
        if (p.anim === "pulse") s.scale.setScalar(p.size * (1 + 0.18 * Math.max(0, S(ph * 7))));
        else if (p.anim === "twinkle") { const v = 0.5 + 0.5 * S(ph * 4); s.scale.setScalar(p.size * (0.4 + 0.8 * v)); s.material.rotation = ph; }
        else if (p.anim === "drift") { const f = (ph * 0.45) % 1; s.position.set(p.at[0], p.at[1] + f * 0.08, p.at[2]); s.material.opacity = 1 - f * f; }
        else if (p.anim === "puff") {
          const f = (ph * 1.1) % 1;
          s.position.set(p.at[0] + p.dir[0] * f * 0.06, p.at[1] + p.dir[1] * f * 0.06, p.at[2] + p.dir[2] * f * 0.06);
          s.scale.setScalar(p.size * (0.55 + 0.9 * f)); s.material.opacity = 1 - f * f;
        }
      }
    },
  };
}
